import { useMutation, useQueryClient } from '@tanstack/react-query';
import { surveysQueryKeys } from '../Constants/queryKeys';
import { resetSurvey } from '../Services/Surveys.service';
import { APPROVAL_LABELS, formatCount } from '../Utils/surveyFormat';
import { useActionToast } from '@/Hooks/useActionToast';

/** "2 versões", "1 versão" — a counter that never prints a bare number next to a guessed noun. */
const countLabel = (total: number, singular: string, plural: string): string =>
  `${formatCount(total)} ${total === 1 ? singular : plural}`;

/**
 * Put a survey back the way it was created (R42) — `POST /:id/reset`, with the slug typed back.
 *
 * **The toast prints the three numbers the endpoint answers with, and that is the point of them.**
 * A "pronto" after an irreversible operation leaves nobody able to check that what happened was
 * what they authorised — and here three different things went: responses, versions and the
 * rewording records of R39. The endpoint reports them separately because they are separate, and so
 * does the confirmation.
 *
 * Everything about this survey is invalidated, by **prefix**. The reset crosses every version: the
 * results of version 2 are not stale, they are gone, and a cached page of respondents would keep
 * rendering rows the BFF no longer has. The detail is invalidated rather than written from the
 * response because the response is a summary, not a survey — the questionnaire the builder has to
 * redraw is v1's, and only a refetch brings it.
 *
 * Nothing is optimistic: a `400` deleted nothing — the slug did not match, or version 1 has no
 * questionnaire — and the screen has to keep saying exactly what it said before.
 */
export const useResetSurvey = (surveyId: string) => {
  const queryClient = useQueryClient();
  const toast = useActionToast();

  return useMutation({
    mutationFn: (confirmSlug: string) => resetSurvey(surveyId, confirmSlug),
    onSuccess: result => {
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.detail(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allResponsesOf(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allResultsOf(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.allCommentsOf(surveyId) });
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.lists() });

      toast.success(
        'Pesquisa resetada',
        `Foram apagadas ${countLabel(result.deletedResponses, 'resposta', 'respostas')}, ` +
          `${countLabel(result.deletedVersions, 'versão', 'versões')} e ` +
          `${countLabel(result.deletedQuestionEdits, 'registro de edição', 'registros de edição')}. ` +
          `Ela volta à versão ${result.version}, com o questionário dela intacto, e o estado de ` +
          `aprovação segue "${APPROVAL_LABELS[result.approvalStatus]}" — resetar não muda isso.`,
      );
    },
    onError: (error: Error) => {
      /*
        Put back in step with the BFF rather than corrected: both `400`s deleted nothing, so the
        numbers come back exactly as they were — the honest answer to a slug that did not match.
      */
      void queryClient.invalidateQueries({ queryKey: surveysQueryKeys.detail(surveyId) });
      toast.failure('Não foi possível resetar a pesquisa', error);
    },
  });
};
